<template>
  <div class="deck-card" @click="handleClick">
    <div class="deck-card__header">
      <div
        class="deck-card__status"
        :class="`deck-card__status--${currentStatus}`"
      >
        <span v-if="isProcessing" class="deck-card__status-dot"></span>
        {{ currentStatus }}
      </div>
      <button class="deck-card__menu" @click.stop="$emit('menu', deck)">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      </button>
    </div>

    <div
      class="deck-card__icon"
      :class="{ 'deck-card__icon--processing': isProcessing }"
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <polygon points="12,2 2,7 12,12 22,7 12,2" />
        <polyline points="2,17 12,22 22,17" />
        <polyline points="2,12 12,17 22,12" />
      </svg>
    </div>

    <h4 class="deck-card__title">
      {{ deck.title || deck.name || "Untitled Deck" }}
    </h4>

    <div class="deck-card__info">
      <span class="deck-card__count">
        {{ cardCountGenerated }}/{{ cardCountRequested }} cards
      </span>
      <span class="deck-card__date">{{
        formatDate(deck.created_at || deck.createdAt)
      }}</span>
    </div>

    <!-- Progress bar for processing -->
    <div v-if="isProcessing" class="deck-card__progress">
      <div class="deck-card__progress-bar">
        <div
          class="deck-card__progress-fill"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>
      <span class="deck-card__progress-text">{{ progress }}%</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { apiGet } from "../../services/api";
import { OperationStatus } from "../../types";

export default {
  name: "DeckCard",
  props: {
    deck: {
      type: Object,
      required: true,
    },
  },
  emits: ["click", "menu", "status-changed"],
  setup(props, { emit }) {
    const currentStatus = ref(props.deck.status);
    const progress = ref(0);
    const cardCountGenerated = ref(
      props.deck.card_count_generated || props.deck.cardCountGenerated || 0
    );
    const cardCountRequested = ref(
      props.deck.card_count_requested ||
        props.deck.cardCountRequested ||
        props.deck.card_count ||
        0
    );

    let pollingInterval = null;
    let pollingStartTime = null;
    const POLLING_INTERVAL = 3000; // 3 seconds
    const POLLING_TIMEOUT = 180000; // 3 minutes

    const isProcessing = computed(
      () =>
        currentStatus.value === OperationStatus.PENDING ||
        currentStatus.value === OperationStatus.PROCESSING
    );

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    };

    const pollStatus = async () => {
      // Check timeout
      if (pollingStartTime && Date.now() - pollingStartTime > POLLING_TIMEOUT) {
        stopPolling();
        return;
      }

      try {
        const result = await apiGet(
          `flash-cards/decks/${props.deck.id}/status`
        );

        if (result.data) {
          const newStatus = result.data.status;

          // Update progress
          if (result.data.progress !== undefined) {
            progress.value = Math.round(result.data.progress * 100);
          } else if (isProcessing.value) {
            progress.value = Math.min(progress.value + 5, 90);
          }

          // Update card counts
          if (result.data.cards_generated !== undefined) {
            cardCountGenerated.value = result.data.cards_generated;
          }

          // Check if status changed
          if (newStatus !== currentStatus.value) {
            currentStatus.value = newStatus;
            emit("status-changed", { id: props.deck.id, status: newStatus });

            // Stop polling if completed or failed
            if (
              newStatus === OperationStatus.COMPLETED ||
              newStatus === OperationStatus.FAILED
            ) {
              progress.value =
                newStatus === OperationStatus.COMPLETED ? 100 : 0;
              stopPolling();
            }
          }
        }
      } catch (e) {
        console.error("[DeckCard] Polling error:", e);
      }
    };

    const startPolling = () => {
      if (pollingInterval) return;
      pollingStartTime = Date.now();
      pollingInterval = setInterval(pollStatus, POLLING_INTERVAL);
      pollStatus(); // Initial poll
    };

    const stopPolling = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };

    const handleClick = () => {
      emit("click", { deck: props.deck, status: currentStatus.value });
    };

    // Watch for deck changes
    watch(
      () => props.deck.status,
      (newStatus) => {
        currentStatus.value = newStatus;
        if (isProcessing.value && !pollingInterval) {
          startPolling();
        }
      }
    );

    onMounted(() => {
      if (isProcessing.value) {
        startPolling();
      }
    });

    onUnmounted(() => {
      stopPolling();
    });

    return {
      currentStatus,
      progress,
      cardCountGenerated,
      cardCountRequested,
      isProcessing,
      formatDate,
      handleClick,
    };
  },
};
</script>

<style scoped>
.deck-card {
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: white;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e5e7eb;
}

.deck-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.deck-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
}

.deck-card__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
  padding: 4px 8px;
  border-radius: 12px;
}

.deck-card__status--completed {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.deck-card__status--processing,
.deck-card__status--pending {
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
}

.deck-card__status--failed {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.deck-card__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.deck-card__menu {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.2s ease;
}

.deck-card__menu:hover {
  background: #f3f4f6;
  color: #374151;
}

.deck-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
  color: #6366f1;
}

.deck-card__icon--processing {
  animation: spin 3s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.deck-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.deck-card__info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.deck-card__count {
  font-size: 12px;
  color: #6b7280;
}

.deck-card__date {
  font-size: 11px;
  color: #9ca3af;
}

.deck-card__progress {
  margin-top: 12px;
  width: 100%;
}

.deck-card__progress-bar {
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.deck-card__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.deck-card__progress-text {
  display: block;
  text-align: center;
  font-size: 10px;
  color: #6b7280;
  margin-top: 4px;
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .deck-card,
[data-theme="dark"] .deck-card,
.dark .deck-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .deck-card:hover,
[data-theme="dark"] .deck-card:hover,
.dark .deck-card:hover {
  box-shadow: 0 8px 24px rgba(0, 196, 68, 0.2);
}

[data-bs-theme="dark"] .deck-card__menu,
[data-theme="dark"] .deck-card__menu,
.dark .deck-card__menu {
  color: #94a3b8;
}

[data-bs-theme="dark"] .deck-card__menu:hover,
[data-theme="dark"] .deck-card__menu:hover,
.dark .deck-card__menu:hover {
  background: rgba(0, 196, 68, 0.15);
  color: #00c444;
}

[data-bs-theme="dark"] .deck-card__icon,
[data-theme="dark"] .deck-card__icon,
.dark .deck-card__icon {
  color: #00c444;
}

[data-bs-theme="dark"] .deck-card__title,
[data-theme="dark"] .deck-card__title,
.dark .deck-card__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .deck-card__count,
[data-theme="dark"] .deck-card__count,
.dark .deck-card__count {
  color: #94a3b8;
}

[data-bs-theme="dark"] .deck-card__date,
[data-theme="dark"] .deck-card__date,
.dark .deck-card__date {
  color: #64748b;
}

[data-bs-theme="dark"] .deck-card__progress-bar,
[data-theme="dark"] .deck-card__progress-bar,
.dark .deck-card__progress-bar {
  background: #475569;
}

[data-bs-theme="dark"] .deck-card__progress-fill,
[data-theme="dark"] .deck-card__progress-fill,
.dark .deck-card__progress-fill {
  background: linear-gradient(90deg, #00c444, #00a83b);
}

[data-bs-theme="dark"] .deck-card__progress-text,
[data-theme="dark"] .deck-card__progress-text,
.dark .deck-card__progress-text {
  color: #94a3b8;
}
</style>
